import React, { useEffect, useState } from 'react';
import { useRoutesStore } from '../../../../store/routesStore';
import { apiClient } from '../../../../services/api';
import styles from './EditRouteModal.module.css';

interface DriverOption { id: number; full_name: string; email: string }

interface CreateRouteModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Minimal "Create Delivery Route" modal — name + date + optional driver.
 * Stops (orders) are added afterwards via the AddOrderToRouteModal from the
 * row kebab on the Routes table. Backend: POST /delivery-routes.
 */
export const CreateRouteModal: React.FC<CreateRouteModalProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [driverId, setDriverId] = useState('');
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createRoute = useRoutesStore((s) => s.createRoute);

  useEffect(() => {
    if (!open) return;
    setName(''); setDate(new Date().toISOString().slice(0, 10));
    setDriverId(''); setError(null); setSubmitting(false);
    setLoadingDrivers(true);
    apiClient
      .get<any>('/users', { limit: 100 })
      .then((res) => {
        const list: any[] = Array.isArray(res) ? res : res?.data ?? res?.users ?? [];
        const onlyDrivers = list.filter((u) => String(u?.role || '').toLowerCase() === 'driver');
        setDrivers(onlyDrivers.map((u) => ({ id: u.id, full_name: u.full_name, email: u.email })));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load drivers'))
      .finally(() => setLoadingDrivers(false));
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Route name is required'); return; }
    if (!driverId) { setError('Pick a driver — the route table requires one'); return; }
    setError(null); setSubmitting(true);
    try {
      await createRoute({
        route_name: name.trim(),
        scheduled_date: date || undefined,
        driver_id: Number(driverId),
        // Stops are added after creation via the row's "Add Order" action,
        // so we start with empty sequence + windows. Backend defaults to [].
        delivery_sequence: [],
        delivery_time_windows: [],
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create route');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Create Delivery Route</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}
          <p className={styles.note}>
            Create the route first, then use the row's kebab → "Add Order" to attach picked orders as stops.
          </p>
          <label className={styles.field}>
            <span>Route Name <span className={styles.required}>*</span></span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Downtown Run – Monday"
              maxLength={120}
              required
            />
          </label>
          <label className={styles.field}>
            <span>Scheduled Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className={styles.field}>
            <span>Assigned Driver <span className={styles.required}>*</span></span>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)} disabled={loadingDrivers} required>
              <option value="">{loadingDrivers ? 'Loading…' : '— Select a driver —'}</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{d.full_name} ({d.email})</option>
              ))}
            </select>
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Route'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRouteModal;
